import NewWindowSvg from 'assets/images/new-window.svg'
import { LightGrayButton, PlainButton } from 'components/Button'
import I18n from 'components/I18n'
import iconColor from 'components/iconColor'
import FasMobileAlt from 'components/Legacy/Icons/FasMobileAlt'
import Popover from 'components/Popover'
import Text from 'components/Text'
import { actions, selectors } from 'data'
import { UserType } from 'data/types'
import messages from 'lang'
import MobileInstallModal from 'layouts/Header/MobileInstallModal'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { color, getWalletLoginUrl, transition } from 'utils'

import { showDepositModal, showWithdrawalModal } from '../../menuActions'
import PageNavigation from '../PageNavigation'
import { Menu, MenuItem, MenuItemSeperator, MenuLink, OutsideLink, renderTrigger } from './common'

const ButtonContainer = styled.div`
  margin-right: 0.5rem;

  &:hover {
    text-decoration: none;
  }
`

const IconWrapper = styled.div`
  display: flex;
`

const GrayButton = styled(LightGrayButton)`
  padding: 0 0.75rem;
`

const Separator = styled.div`
  width: 1px;
  height: 1rem;
  border-left: 1px solid ${color('dark600')};
  align-self: center;
  margin-right: 1rem;
`

const IconButton = styled(PlainButton)`
  &:nth-child(2) {
    border: solid 1px ${color('dark600')};
    border-top: none;
    border-bottom: none;
    border-radius: 0;
  }

  svg {
    transition: fill ${transition('medium')};
    height: 1rem;
    width: 1rem;
    ${iconColor('overlayWhite400', 'overlayWhite600')}

    &:hover {
      cursor: pointer;
    }
  }
`

const ModalWrapper = styled.div`
  background-color: ${color('dark800')};
  border-radius: 0.75rem;
`

const RightMenu = styled.div`
  display: flex;
  align-items: center;
`

type PropsType = {
  hasMoney: boolean
  user: UserType
  walletGuid?: string
  walletUrl: string
}

const DesktopSignedIn = ({ hasMoney, user, walletGuid, walletUrl }: PropsType) => {
  const [isActive, setActive] = React.useState<boolean>(false)
  const [showQrModal, setShowQrModal] = React.useState<boolean>(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isSilver = useSelector(selectors.auth.isUserSilverishTier)

  const redirectToSettings = () => navigate('/settings')
  const redirectToHistory = () => navigate('/history')

  // Utility function that closes the popover and then calls passed FX
  const closeAnd = (fx) => () => {
    setActive(false)
    fx(dispatch)
  }

  return (
    <>
      <PageNavigation />

      <RightMenu>
        {hasMoney && (
          <ButtonContainer>
            <GrayButton e2e='withdrawButton' onClick={closeAnd(showWithdrawalModal)} size='sm'>
              <Text color='white' size='sm' weight={600}>
                <I18n data={messages.button_withdraw} />
              </Text>
            </GrayButton>
          </ButtonContainer>
        )}
        {isSilver ? (
          <ButtonContainer>
            <GrayButton
              e2e='upgradeButton'
              onClick={() => {
                dispatch(actions.app.showModal('TIER_TWO'))
              }}
              size='sm'
            >
              <Text color='white' size='sm' weight={600}>
                <I18n data={messages.upgrade} />
              </Text>
            </GrayButton>
          </ButtonContainer>
        ) : (
          <ButtonContainer>
            <GrayButton e2e='depositButton' onClick={closeAnd(showDepositModal)} size='sm'>
              <Text color='white' size='sm' weight={600}>
                <I18n data={messages.button_deposit} />
              </Text>
            </GrayButton>
          </ButtonContainer>
        )}

        <IconWrapper>
          <IconButton
            onClick={() => {
              setShowQrModal(true)
              dispatch(
                actions.analytics.trackEvent({
                  key: 'Mobile App Install Icon Clicked',
                  properties: {}
                })
              )
            }}
            size='sm'
          >
            <FasMobileAlt />
          </IconButton>
          {showQrModal && <MobileInstallModal setShowQrModal={setShowQrModal} />}

          <ModalWrapper />
          <Separator />
        </IconWrapper>

        <Popover
          active={isActive}
          e2e='navigatePopover'
          onVisibleToggle={() => setActive(!isActive)}
          renderTrigger={renderTrigger}
        >
          <Menu>
            <MenuItem>
              <MenuLink
                data-e2e='goToSettingsPage'
                onClick={closeAnd(redirectToSettings)}
                weight={600}
              >
                <I18n data={messages.settings} />
              </MenuLink>
            </MenuItem>

            <MenuItem>
              <MenuLink
                data-e2e='goToHistoryPage'
                onClick={() => {
                  dispatch(
                    actions.analytics.trackEvent({
                      key: 'History Clicked',
                      properties: {
                        origin: 'NAVIGATION',
                        type: 'TRADE'
                      }
                    })
                  )
                  closeAnd(redirectToHistory)()
                }}
                weight={600}
              >
                <I18n data={messages.history} />
              </MenuLink>
            </MenuItem>

            {walletGuid ? (
              <MenuItem>
                <MenuLink
                  data-e2e='linkWallet'
                  href={getWalletLoginUrl(walletUrl, walletGuid, user.email)}
                  target='_blank'
                  weight={600}
                >
                  <I18n data={messages.go_to_wallet} />
                  &nbsp;
                  <OutsideLink src={NewWindowSvg} />
                </MenuLink>
              </MenuItem>
            ) : null}

            <MenuItemSeperator />

            <MenuItem>
              <MenuLink
                data-e2e='headerSignout'
                onClick={() => {
                  dispatch(
                    actions.analytics.trackEvent({
                      key: 'Signed Out',
                      properties: { origin: 'NAVIGATION' }
                    })
                  )
                  navigate('/trade')
                  dispatch(actions.auth.signout())
                }}
              >
                <I18n data={messages.button_signout} />
              </MenuLink>
            </MenuItem>
          </Menu>
        </Popover>
      </RightMenu>
    </>
  )
}

export default DesktopSignedIn
